import ExplainDialog from '@/app/_lens-components/components/ExplainDialog';
import LensTypePrice from '@/app/_lens-components/components/LensTypePrice';
import { LENS_COLOR } from '@/app/_lens-components/constants';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import { BrandItem, ColorItem, LensShowItem, LensShowResponse } from '@/app/_lens-components/types';
import { getApiPrescriptionType, handleColorItemPriceInfo } from '@/app/_lens-components/utils';
import Button from '@/components/Button';
import QuestionResponsive from '@/components/IconResponsive/QuestionResponsive';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import ProductImageWithLenses from '@/components/Product/ProductImageWithLenses';
import Tooltip from '@/components/Tooltip';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { processLineBreaks } from '@/utils/tools';
import { Skeleton, useMediaQuery } from '@mui/material';
import { CloseOutlined, CollapseArrowDownOutlined, RightArrowOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

// 临时InfiniteScroll组件，后续需要实现
// const InfiniteScroll = ({ children, onScroll, className }: any) => (
//   <div
//     className={className}
//     onScroll={(e) => {
//       // 阻止滚动事件冒泡
//       e.stopPropagation();
//       onScroll && onScroll(e.currentTarget.scrollTop);
//     }}
//   >
//     {children}
//   </div>
// );

interface LensTypeProps {
  isFrameProcess: boolean;
  lensTypeColorData: LensShowResponse;
  selectedLensType?: LensShowItem;
  selectedBrand?: BrandItem;
  selectedColorItem?: ColorItem;
  onSelectedLensType?: (lensType: LensShowItem) => void;
  onSelectedBrand?: (brand: BrandItem) => void;
  onSelectedColorItem?: (colorItem: ColorItem) => void;
  onBackToEditPrescription?: () => void;
}

// 镜片类型曝光追踪组件
const LensTypeExpTracker: React.FC<{
  item: LensShowItem;
  onExposure: (colorItem: ColorItem | null, lens: LensShowItem) => void;
  exposedItems: React.MutableRefObject<Set<string>>;
}> = ({ item, onExposure, exposedItems }) => {
  const [ref, inView] = useInView({
    threshold: 0.5, // 50%的元素可见时触发
    triggerOnce: false, // 允许检测，但用 ref 控制曝光逻辑
  });

  useEffect(() => {
    if (inView) {
      // 生成镜片类型的唯一标识
      const itemKey = `${item.lens_type}_${item.lens_name}`;

      // 只有未曝光过的项目才触发曝光事件
      if (!exposedItems.current.has(itemKey)) {
        exposedItems.current.add(itemKey);

        // 获取默认颜色项目
        let defaultColorItem = null;
        if (item.merge_brand === 2) {
          // 非品牌类型，使用镜片类型的默认颜色
          if (item.default_lens_color) {
            defaultColorItem = item.color_list?.find(
              (color) => color.lens_color === item.default_lens_color,
            );
          } else if (item.color_list?.length) {
            defaultColorItem = item.color_list[0];
          }
        }

        onExposure(defaultColorItem, item);
      }
    }
  }, [inView]);

  return <div ref={ref} className="absolute inset-0 pointer-events-none" />;
};

// TODO 弹层的商品展示未实现。等Footer层实现了做成组件复用过来
const LensType: React.FC<LensTypeProps> = ({
  isFrameProcess,
  lensTypeColorData,
  selectedLensType,
  selectedBrand,
  selectedColorItem,
  onSelectedLensType,
  onSelectedBrand,
  onSelectedColorItem,
  onBackToEditPrescription,
}) => {
  const t = useTranslations('lens-process');
  const { trackEvent } = useReporting();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  // 占位状态，实际实现时需要替换
  const [showExplainPop, setShowExplainPop] = useState(false);
  const [explainContent, setExplainContent] = useState<{
    brand_name?: string;
    lens_name?: string;
    explain?: string;
  }>({});
  const waiMode = useStore((state) => state.waiMode);
  const {
    lensGoodsItem,
    lensGoodsSensorsData,
    prescriptionType,
    presTypeOptionName,
    hasIndexPrice,
    isLoadingLensShow,
    brandExpand,
    setBrandExpand,
    showChooseColor,
    setShowChooseColor,
    setPrescriptionType,
    setPresTypeOptionName,
  } = useLensProcessStore();
  // 1280px 跟 className 里的 xl:xxx 统一
  const isWap = useMediaQuery('(max-width:1280px)');

  // 占位函数，实际实现时需要替换
  // const handleScroll = (top: number) => {
  //   logger.log('👀', 'handleScroll', top);
  // };

  // 实时处方类型。解决 react 异步set的问题。
  const presTypeRef = useRef(
    getApiPrescriptionType(prescriptionType) || EPrescriptionType.FRAME_ONLY,
  );
  const presTypeOptionNameRef = useRef(presTypeOptionName || EPrescriptionType.FRAME_ONLY);

  // 离开页面时，关闭颜色选择层
  useEffect(() => {
    return () => {
      setShowChooseColor(false);
    };
  }, []);

  const handleSetShowChooseColor = (
    show: boolean,
    lens: LensShowItem,
    colorItem: ColorItem,
    brand?: BrandItem,
  ) => {
    if (isWap) {
      setShowChooseColor(show);

      SDLensTypePageColorPopUpExp(lens, colorItem, brand);
    }
  };

  const handleSetPrescriptionType = (item: LensShowItem) => {
    if (isFrameProcess) {
      const presType =
        item.lens_type === EPrescriptionType.FRAME_ONLY
          ? EPrescriptionType.FRAME_ONLY
          : EPrescriptionType.NON_PRESCRIPTION;

      presTypeRef.current = presType;
      presTypeOptionNameRef.current = presType;

      setPrescriptionType(presType);
      setPresTypeOptionName(presType);
    }
  };

  /*
   * 点击事件处理-----开始
   */
  // 镜片类型点击
  const onLensClick = (item: LensShowItem, index: number) => {
    onSelectedLensType(item);
    handleSetPrescriptionType(item);

    if (item.merge_brand === 1) {
      // 处理品牌层的展开与收起
      const lensTypeKey = getLensTypeKey(item);
      if (brandExpand[lensTypeKey]) {
        setBrandExpand({});
      } else {
        setBrandExpand({ [lensTypeKey]: true });
        const defaultBrand = item.brand_list.find(
          (brand) => brand.brand_title === item.default_brand_title,
        );
        logger.warn('👀', 'hasSelectedBrand', item.default_brand_title, defaultBrand, brandExpand);
        if (defaultBrand) {
          onSelectedBrand(defaultBrand);
          if (defaultBrand.default_lens_color) {
            onSelectedColorItem(
              defaultBrand.color_list?.find(
                (color) => color.lens_color === defaultBrand.default_lens_color,
              ),
            );
          } else {
            onSelectedColorItem(null);
          }
        } else {
          onSelectedBrand(null);
          onSelectedColorItem(null);
        }
        // 把展开内容置于屏幕中间
        const lensTypeOption = document.querySelector(`.lens-type-option-${index}`);
        if (lensTypeOption) {
          setTimeout(() => {
            lensTypeOption.scrollIntoView({
              block: 'start', // 值有start,center,end，nearest，当前显示在视图区域中间
              behavior: 'smooth',
            });
          }, 0);
        }
      }
      SDLensTypePageSelectClick(null, item);
    } else if (hasColorList(item)) {
      onSelectedBrand(null);
      setBrandExpand({});
      let colorItem = item.color_list[0];
      if (item.default_lens_color) {
        colorItem = item.color_list.find((color) => color.lens_color === item.default_lens_color);
      }
      onSelectedColorItem(colorItem);
      // 展开颜色层
      handleSetShowChooseColor(true, item, colorItem);

      SDLensTypePageSelectClick(colorItem, item);
    } else {
      onSelectedBrand(null);
      setBrandExpand({});
      // 直接选中
      onSelectedColorItem(item.color_list[0]);
      SDLensTypePageSelectClick(item.color_list[0], item);
    }
  };
  // 品牌点击
  const onLensBrandClick = (brandItem: BrandItem, item: LensShowItem) => {
    onSelectedBrand(brandItem);
    item.default_brand_title = brandItem.brand_title;

    // 合并品牌类型，添加子品牌的lens_type到sd_lens_type字段，埋点使用
    if (item.merge_lens_type) {
      item.sd_lens_type = brandItem.lens_type;
    }
    onSelectedLensType(item);
    if (hasColorList(brandItem)) {
      let colorItem = brandItem.color_list[0];
      if (brandItem.default_lens_color) {
        colorItem = brandItem.color_list.find(
          (color) => color.lens_color === brandItem.default_lens_color,
        );
      }
      onSelectedColorItem(colorItem);
      handleSetShowChooseColor(true, selectedLensType, colorItem, brandItem);

      SDLensTypePageBrandSelectClick(brandItem, colorItem);
    } else {
      onSelectedColorItem(brandItem.color_list[0]);

      SDLensTypePageBrandSelectClick(brandItem, brandItem.color_list[0]);
    }
  };
  // 页面上非品牌颜色点击
  const onLensColorClick = (item: LensShowItem, colorItem: ColorItem) => {
    // 清空品牌相关信息
    onSelectedBrand(null);
    setBrandExpand({});

    item.default_lens_color = colorItem.lens_color;
    onSelectedLensType(item);
    handleSetPrescriptionType(item);
    onSelectedColorItem(colorItem);
    handleSetShowChooseColor(true, item, colorItem);

    SDLensTypePageSelectClick(colorItem, item);
  };
  // 页面上品牌层颜色点击
  const onLensBrandColorClick = (
    colorItem: ColorItem,
    brandItem: BrandItem,
    item: LensShowItem,
  ) => {
    brandItem.default_lens_color = colorItem.lens_color;

    // 合并品牌类型，添加子品牌的lens_type到sd_lens_type字段，埋点使用
    if (item.merge_lens_type) {
      item.sd_lens_type = brandItem.lens_type;
    }

    onSelectedLensType(item);
    handleSetPrescriptionType(item);
    onSelectedBrand(brandItem);
    onSelectedColorItem(colorItem);
    handleSetShowChooseColor(true, item, colorItem, brandItem);
    item.default_brand_title = brandItem.brand_title;
    SDLensTypePageBrandSelectClick(brandItem, colorItem);
  };
  // 弹窗中颜色点击
  const onPopupLensColorClick = (colorItem: ColorItem) => {
    if (selectedLensType?.merge_brand === 2) {
      selectedLensType.default_lens_color = colorItem.lens_color;
    } else if (selectedBrand) {
      selectedBrand.default_lens_color = colorItem.lens_color;
    }
    onSelectedColorItem(colorItem);

    if (isWap) {
      SDLensTypePageColorPopUpClick(selectedLensType, colorItem, selectedBrand);
    }
  };
  /*
   * 点击事件处理-----结束
   */

  /*
   * 选中状态判断————开始
   */
  // 检查颜色是否选中
  const checkColorSelected = (item: LensShowItem, brandItem: BrandItem, colorItem: ColorItem) => {
    let brandCheckResult = true;
    if (selectedLensType?.merge_brand === 1 && selectedBrand) {
      brandCheckResult =
        brandItem?.brand_title === selectedBrand?.brand_title &&
        brandItem?.brand_name === selectedBrand?.brand_name;
    }
    return (
      item?.lens_type === selectedLensType?.lens_type &&
      item?.lens_name === selectedLensType?.lens_name &&
      brandCheckResult &&
      selectedColorItem?.lens_color === colorItem?.lens_color
    );
  };

  const hasColorList = (item: LensShowItem | BrandItem) => {
    // 如果有颜色列表
    if (Array.isArray(item.color_list) && item.color_list.length) {
      // 颜色列表的长度为1，并且这个颜色为non 不展示
      return !(item.color_list.length === 1 && item.color_list[0].lens_color === LENS_COLOR.NON);
    }
    return false;
  };

  // 获取当前活跃的镜片数据源（品牌或类型）
  const getActiveSource = (): BrandItem | LensShowItem => {
    if (selectedLensType?.merge_brand === 1 && selectedBrand) {
      return selectedBrand;
    }
    return selectedLensType;
  };
  /*
   * 选中状态判断————结束
   */

  // 生成镜片类型的唯一键
  const getLensTypeKey = (item: LensShowItem) => {
    return `${item.lens_type}_${item.lens_name}`;
  };

  const showExplain = (item: any) => {
    setShowExplainPop(true);
    setExplainContent(item);
  };

  function SDLensTypePageSelectClick(colorItem?: ColorItem, lens?: LensShowItem) {
    try {
      let lens_color = '';
      let lens_price = null;
      let current_lens_price = null;
      let currencyName = '';
      if (colorItem) {
        lens_color = colorItem.color_name;
        const lensPriceInfo = handleColorItemPriceInfo(
          colorItem,
          hasIndexPrice || isFrameProcess,
          currency,
          userInfo,
        );
        lens_price = parseFloat(lensPriceInfo.price);
        current_lens_price = parseFloat(lensPriceInfo.sell_price);
        currencyName = currency.name;
      }
      /** 仅无处方配镜 / 仅镜框配镜：单色折射率场景与镀膜页埋点同源，取颜色下默认镜片 id/code */
      const lensFromColor = colorItem?.lens_list?.[0];
      const resolvedPresType = getApiPrescriptionType(prescriptionType) ?? presTypeRef.current;
      const shouldReportLensSku =
        resolvedPresType === EPrescriptionType.NON_PRESCRIPTION ||
        resolvedPresType === EPrescriptionType.FRAME_ONLY;
      trackEvent(
        {
          eventName: 'LensTypePage_Select_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: presTypeRef.current,
            prescription_type_name: presTypeOptionNameRef.current,
            lens_type_name: lens?.lens_name,
            lens_type: selectedLensType?.sd_lens_type || lens?.lens_type,
            lens_color,
            lens_price,
            current_lens_price,
            currency: currencyName,
            ...(shouldReportLensSku
              ? {
                  lens_id: lensFromColor?.id != null ? `${lensFromColor.id}` : '',
                  lens_code: lensFromColor?.code ?? '',
                }
              : {}),
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  function SDLensTypePageSelectExp(colorItem?: ColorItem, lens?: LensShowItem) {
    try {
      let lens_color = '';
      let lens_price = null;
      let current_lens_price = null;
      let currencyName = '';
      if (colorItem) {
        lens_color = colorItem.color_name;
        const lensPriceInfo = handleColorItemPriceInfo(
          colorItem,
          hasIndexPrice,
          currency,
          userInfo,
        );
        lens_price = parseFloat(lensPriceInfo.price);
        current_lens_price = parseFloat(lensPriceInfo.sell_price);
        currencyName = currency.name;
      }
      trackEvent(
        {
          eventName: 'LensTypePage_Select_Exp',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: presTypeRef.current,
            prescription_type_name: presTypeOptionNameRef.current,
            lens_type_name: lens?.lens_name,
            lens_type: lens?.lens_type,
            lens_color,
            lens_price,
            current_lens_price,
            currency: currencyName,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  useEffect(() => {
    try {
      setTimeout(() => {
        trackEvent(
          {
            eventName: 'LensTypePage_View',
            properties: {
              ...(lensGoodsSensorsData || {}),
              prescription_type: presTypeRef.current,
              prescription_type_name: presTypeOptionNameRef.current,
            },
          },
          [ProviderType.SENSORS],
        );
      }, 500); // 延迟500ms. 1. 让相关数据就绪；2. 让 EditLensPage_View 事件先触发
    } catch (e) {
      logger.error('👀', e);
    }
  }, []);

  function SDLensTypePageBrandSelectClick(brand?: BrandItem, colorItem?: ColorItem) {
    try {
      let lens_color = '';
      let lens_price = null;
      let current_lens_price = null;
      let currencyName = '';
      if (colorItem) {
        lens_color = colorItem.color_name;
        const lensPriceInfo = handleColorItemPriceInfo(
          colorItem,
          hasIndexPrice || isFrameProcess,
          currency,
          userInfo,
        );
        lens_price = parseFloat(lensPriceInfo.price);
        current_lens_price = parseFloat(lensPriceInfo.sell_price);
        currencyName = currency.name;
      }
      // 有的场景下折射率只能从colorItem中拿默认的
      const lensFromColor = colorItem?.lens_list?.[0];
      const resolvedPresType = getApiPrescriptionType(prescriptionType) ?? presTypeRef.current;
      const shouldReportLensSku =
        resolvedPresType === EPrescriptionType.NON_PRESCRIPTION ||
        resolvedPresType === EPrescriptionType.FRAME_ONLY;
      trackEvent(
        {
          eventName: 'LensTypePage_BrandSelect_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: presTypeRef.current,
            prescription_type_name: presTypeOptionNameRef.current,
            lens_type_name: selectedLensType?.lens_name,
            lens_type: selectedLensType?.sd_lens_type || selectedLensType?.lens_type,
            lens_brand_name: brand.brand_name,
            lens_brand: brand.brand_title,
            lens_color,
            lens_price,
            current_lens_price,
            currency: currencyName,
            ...(shouldReportLensSku
              ? {
                  lens_id: lensFromColor?.id != null ? `${lensFromColor.id}` : '',
                  lens_code: lensFromColor?.code ?? '',
                }
              : {}),
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  function SDLensTypePageColorPopUpExp(
    lens: LensShowItem,
    colorItem: ColorItem,
    brand?: BrandItem,
  ) {
    SDLensTypePageColorPopUpEvent('LensTypePage_ColorPopUp_Exp', lens, colorItem, brand);
  }

  function SDLensTypePageColorPopUpClick(
    lens: LensShowItem,
    colorItem: ColorItem,
    brand?: BrandItem,
  ) {
    SDLensTypePageColorPopUpEvent('LensTypePage_ColorPopUp_Click', lens, colorItem, brand);
  }

  function SDLensTypePageColorPopUpEvent(
    eventName: string,
    lens: LensShowItem,
    colorItem: ColorItem,
    brand?: BrandItem,
  ) {
    try {
      let lens_color = '';
      let lens_price = null;
      let current_lens_price = null;
      let currencyName = '';
      if (colorItem) {
        lens_color = colorItem.color_name;
        const lensPriceInfo = handleColorItemPriceInfo(
          colorItem,
          hasIndexPrice,
          currency,
          userInfo,
        );
        lens_price = parseFloat(lensPriceInfo.price);
        current_lens_price = parseFloat(lensPriceInfo.sell_price);
        currencyName = currency.name;
      }
      trackEvent(
        {
          eventName,
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: presTypeRef.current,
            prescription_type_name: presTypeOptionNameRef.current,
            lens_type_name: lens.lens_name,
            lens_type: selectedLensType?.sd_lens_type || lens.lens_type,
            lens_brand: brand?.brand_title,
            lens_brand_name: brand?.brand_name,
            lens_color,
            lens_price,
            current_lens_price,
            currency: currencyName,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  const checkLensTypeSelected = (item: LensShowItem) => {
    // 对于merge_brand === 2的情况，检查lens_type和lens_name是否匹配
    const isLensSelected =
      selectedLensType?.lens_type === item.lens_type &&
      selectedLensType?.lens_name === item.lens_name &&
      selectedLensType?.merge_brand === 2;

    // 对于merge_brand === 1的情况，需要额外检查品牌选择. 注：没选中子选项时，也需要有选中样式。
    const isMergedLensSelected =
      !isWap &&
      selectedLensType?.lens_type === item.lens_type &&
      selectedLensType?.lens_name === item.lens_name &&
      selectedLensType?.merge_brand === 1 &&
      (selectedLensType.brand_list.find(
        (brand) => brand.brand_title === selectedBrand?.brand_title,
      ) ||
        !selectedBrand);

    return isLensSelected || isMergedLensSelected;
  };

  // PC wap 价格展示位置不同，没办法通过断点来复用
  const getResponsiveLensTypePrice = (item: LensShowItem) => {
    if (Array.isArray(item.color_list) && item.color_list.length && item.merge_brand === 2) {
      return (
        <div className={stepCommonClassName.priceContainer}>
          <LensTypePrice
            colorList={item.color_list}
            hasIndexPrice={hasIndexPrice}
            renderPrice={(price, sellPrice) =>
              Number(sellPrice?.replace(/[^0-9.]/g, '')) > 0 ? (
                <span className={stepCommonClassName.priceInnerBox}>
                  <span
                    className={`${stepCommonClassName.priceText} ${
                      price !== sellPrice ? 'sale' : ''
                    }`}
                  >
                    {`${sellPrice}`}
                  </span>
                  <span
                    className={stepCommonClassName.originalPriceText(waiMode)}
                  >{`${price}`}</span>
                </span>
              ) : (
                <span
                  className={`${stepCommonClassName.priceText} ${
                    price !== sellPrice ? 'sale' : ''
                  }`}
                >
                  {t('free')}
                </span>
              )
            }
          />
        </div>
      );
    }
    return null;
  };
  const [lensTypeContainer, setLensTypeContainer] = useState<HTMLElement>(null);

  // 记录已曝光的镜片类型项目，防止重复曝光
  const exposedItemsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    setLensTypeContainer(document.querySelector('#lens-process-type') as HTMLElement);
  }, []);

  // 组件卸载时清空曝光记录，支持重新进入时再次曝光
  useEffect(() => {
    return () => {
      exposedItemsRef.current.clear();
    };
  }, []);
  const getResponsiveBrandTypePrice = (brandItem: BrandItem) => {
    if (Array.isArray(brandItem.color_list) && brandItem.color_list.length) {
      return (
        <div className={stepCommonClassName.priceContainer}>
          <LensTypePrice
            colorList={brandItem.color_list}
            hasIndexPrice={hasIndexPrice}
            renderPrice={(price, sellPrice) =>
              Number(sellPrice?.replace(/[^0-9.]/g, '')) > 0 ? (
                <span className={stepCommonClassName.priceInnerBox}>
                  <span
                    className={`${stepCommonClassName.priceText} ${
                      price !== sellPrice ? 'sale' : ''
                    }`}
                  >
                    {`${sellPrice}`}
                  </span>
                  <span
                    className={stepCommonClassName.originalPriceText(waiMode)}
                  >{`${price}`}</span>
                </span>
              ) : (
                <span
                  className={`${stepCommonClassName.priceText} ${price !== sellPrice ? 'sale' : ''}`}
                >
                  {t('free')}
                </span>
              )
            }
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div id="lens-process-type" className="w-full h-full box-border overflow-y-hidden font-br">
      {/* <InfiniteScroll className="my-scroll" onScroll={handleScroll}> */}
      <h3 className={stepCommonClassName.stepTitle}>
        <span>{t('lensType')}</span>
      </h3>
      {(() => {
        // 加载中显示骨架屏
        if (isLoadingLensShow) {
          return (
            <div className={stepCommonClassName.optionsContainer}>
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className={stepCommonClassName.optionItem}>
                  <div className={stepCommonClassName.optionBox}>
                    <div className={stepCommonClassName.optionContent}>
                      {/* 左侧图片骨架屏 */}
                      <div className={`${stepCommonClassName.imageContainer} min-w-[60px]`}>
                        <Skeleton variant="rectangular" width={60} height={60} animation="wave" />
                      </div>
                      {/* 右侧内容骨架屏 */}
                      <div className={stepCommonClassName.textContainer}>
                        <div className={stepCommonClassName.titleContainer}>
                          <Skeleton variant="text" width="60%" height={20} animation="wave" />
                        </div>
                        <div className={stepCommonClassName.priceContainer}>
                          <Skeleton variant="text" width="40%" height={16} animation="wave" />
                        </div>
                        <div className={stepCommonClassName.colorSelector}>
                          {Array.from({ length: 4 }).map((__, colorIndex) => (
                            <Skeleton
                              key={colorIndex}
                              variant="circular"
                              width={22}
                              height={22}
                              animation="wave"
                              style={{ marginRight: 12, marginBottom: 5 }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        }

        // 有数据时显示列表
        if (Array.isArray(lensTypeColorData) && lensTypeColorData.length) {
          return (
            <ul className={stepCommonClassName.optionsContainer}>
              {lensTypeColorData.map((item, index) => (
                <li
                  key={index}
                  id={item.merge_brand === 1 ? 'brand-box' : ''}
                  className={`${stepCommonClassName.optionItem} ${item.recommend === 1 ? stepCommonClassName.recommendedItem : ''}`}
                >
                  {/* 曝光追踪组件 */}
                  <LensTypeExpTracker
                    item={item}
                    onExposure={SDLensTypePageSelectExp}
                    exposedItems={exposedItemsRef}
                  />
                  {item.recommend === 1 && (
                    <div className={stepCommonClassName.recommendBadge}>
                      <div className={stepCommonClassName.recommendBadgeContent}>
                        <span>{t('recommendedForYou')}</span>
                      </div>
                    </div>
                  )}
                  <div
                    className={`lens-type-option-${index} ${stepCommonClassName.optionBox} ${
                      checkLensTypeSelected(item) ? stepCommonClassName.selectedOptionBox : ''
                    } cursor-pointer`}
                    onClick={() => onLensClick(item, index)}
                  >
                    {item.merge_brand === 1 && (
                      <LensTypePrice
                        colorList={
                          item.brand_list.find((brandItem) => brandItem.brand_title === 'non')
                            ?.color_list || null
                        }
                        hasIndexPrice={hasIndexPrice}
                        renderPrice={(price, sellPrice, discountPercent, hasMemberPrice) =>
                          discountPercent > 0 && (
                            <div
                              className={stepCommonClassName.discountBadge(
                                userInfo,
                                hasMemberPrice,
                              )}
                            >
                              <span className="relative top-[1px]">
                                {t('discountPercentOffSelectStyles', { discountPercent })}
                              </span>
                            </div>
                          )
                        }
                      />
                    )}
                    <div className={`${stepCommonClassName.optionContent} p-[20px_20px_20px_14px]`}>
                      <div className={`${stepCommonClassName.imageContainer} min-w-[60px]`}>
                        <Image
                          src={item.img}
                          alt={item.lens_name}
                          width={60}
                          height={60}
                          className="object-contain w-[60px] h-[60px]"
                        />
                      </div>
                      <div
                        className={`${stepCommonClassName.textContainer} ${
                          item.merge_brand === 1
                            ? 'relative min-h-[50px] flex flex-col items-start justify-center'
                            : ''
                        }`}
                      >
                        {!isWap && item.merge_brand === 1 && (
                          <div
                            className={`absolute right-[0] top-[calc(50%-8px)] transform transition-transform duration-200 text-gray f16 ${
                              !brandExpand[getLensTypeKey(item)] ? '' : 'rotate-180 '
                            }`}
                          >
                            <CollapseArrowDownOutlined className={stepCommonClassName.arrowIcon} />
                          </div>
                        )}

                        <div className={stepCommonClassName.textCenterContainer}>
                          <div className={stepCommonClassName.titleContainer}>
                            {item.type_img_m ? (
                              <Image
                                src={item.type_img_m}
                                className={stepCommonClassName.titleImage}
                                width={192}
                                height={21}
                                alt={item.lens_name}
                              />
                            ) : (
                              <p>{item.lens_name}</p>
                            )}
                            {item.explain && (
                              <Tooltip
                                title={
                                  <p
                                    className="text-[14px]"
                                    dangerouslySetInnerHTML={{
                                      __html: processLineBreaks(item.explain),
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  />
                                }
                                placement="bottom"
                                variant="light"
                                sx={{
                                  zIndex: 1500,
                                  '& .MuiTooltip-tooltip': {
                                    fontSize: '14px',
                                    maxWidth: '490px',
                                  },
                                }}
                              >
                                <QuestionResponsive
                                  className={stepCommonClassName.questionIcon}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    isWap && showExplain(item);
                                  }}
                                />
                              </Tooltip>
                            )}
                            {item.merge_brand === 1 && isWap && (
                              <div
                                className={`absolute right-0 transform transition-transform duration-200  ${
                                  !brandExpand[getLensTypeKey(item)] ? '' : 'rotate-180 '
                                }`}
                              >
                                <CollapseArrowDownOutlined
                                  className={stepCommonClassName.arrowIcon}
                                />
                              </div>
                            )}
                            {!isWap && item.option_img_pc && (
                              <Image
                                className="w-[auto] h-[16px] ml-[8px]"
                                src={item.option_img_pc}
                                width={102}
                                height={16}
                                alt=""
                              />
                            )}
                          </div>
                          {/* TODO 动态图片如何设置宽高 */}
                          {isWap && item.option_img_m && (
                            <Image
                              className="w-[auto] h-[16px] mt-[9px]"
                              src={item.option_img_m}
                              width={102}
                              height={16}
                              alt=""
                            />
                          )}

                          {(() => {
                            let showDescription = false;

                            if (!item.description) return null; // 规则 0 没有描述内容, 则不展示

                            const rule2 =
                              selectedLensType?.lens_type === item.lens_type &&
                              selectedLensType?.lens_name === item.lens_name; // 规则 2 类型和名称匹配
                            const rule3 = brandExpand[getLensTypeKey(item)]; // 规则 3 wap 且品牌展开
                            const rule4 = item.color_list?.length > 1; // 规则 4 颜色列表长度大于1
                            if (isWap) {
                              if (rule4) return null;
                              if (item.merge_brand === 1) {
                                // 是否为品牌
                                showDescription = rule3;
                              } else {
                                showDescription = rule2;
                              }
                            } else {
                              showDescription = true;
                            }

                            if (!showDescription) return null;

                            return (
                              <p
                                className={`${stepCommonClassName.description(waiMode)} max-xl:pr-[0px]`}
                              >
                                {item.description}
                              </p>
                            );
                          })()}
                          {isWap && getResponsiveLensTypePrice(item)}
                          {hasColorList(item) && item.merge_brand === 2 && (
                            <ul className={stepCommonClassName.colorSelector}>
                              {item.color_list?.map((colorItem) => (
                                <li
                                  key={colorItem.lens_color}
                                  className={`${stepCommonClassName.colorItem} ${
                                    checkColorSelected(item, null, colorItem)
                                      ? stepCommonClassName.selectedColorItem
                                      : stepCommonClassName.unselectedColorItem
                                  } cursor-pointer`}
                                  style={
                                    colorItem.lens_color === LENS_COLOR.NON
                                      ? { display: 'none' }
                                      : {}
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onLensColorClick(item, colorItem);
                                  }}
                                >
                                  {isWap ? (
                                    <Image
                                      src={colorItem.option_img}
                                      alt={colorItem.color_name}
                                      width={16}
                                      height={16}
                                    />
                                  ) : (
                                    <Tooltip
                                      title={colorItem.explain || colorItem.color_name}
                                      placement="top"
                                      variant="light"
                                      stopPropagation={false}
                                      sx={{
                                        zIndex: 1500,
                                      }}
                                    >
                                      <Image
                                        src={colorItem.option_img}
                                        alt={colorItem.color_name}
                                        width={16}
                                        height={16}
                                      />
                                    </Tooltip>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        {!isWap && getResponsiveLensTypePrice(item)}

                        {Array.isArray(item.color_list) &&
                          item.color_list.length &&
                          item.merge_brand === 2 && (
                            <LensTypePrice
                              colorList={item.color_list}
                              hasIndexPrice={hasIndexPrice}
                              renderPrice={(price, sellPrice, discountPercent, hasMemberPrice) =>
                                discountPercent > 0 && (
                                  <div
                                    className={stepCommonClassName.discountBadge(
                                      userInfo,
                                      hasMemberPrice,
                                    )}
                                  >
                                    <span className="relative top-[1px]">
                                      {t('discountPercentOff', { discountPercent })}
                                    </span>
                                  </div>
                                )
                              }
                            />
                          )}
                      </div>
                      {hasColorList(item) && item.merge_brand === 2 && isWap && (
                        <div className="pt-[17px] pl-[3px]">
                          <RightArrowOutlined className={stepCommonClassName.arrowIcon} />
                        </div>
                      )}
                    </div>

                    {/* 品牌列表 */}
                    {item.merge_brand === 1 &&
                      item.brand_list?.length > 0 &&
                      selectedLensType?.lens_type === item.lens_type &&
                      selectedLensType?.lens_name === item.lens_name &&
                      brandExpand[getLensTypeKey(item)] && (
                        <div className={stepCommonClassName.brandBox}>
                          {item.brand_list.map((brandItem) => (
                            <div
                              key={brandItem.brand_name}
                              className={`${stepCommonClassName.brandItem} ${
                                brandItem.brand_name === selectedBrand?.brand_name
                                  ? stepCommonClassName.selectedBrandOptionBox
                                  : 'border-transparent'
                              } cursor-pointer`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onLensBrandClick(brandItem, item);
                              }}
                            >
                              <div className={stepCommonClassName.brandItemInner}>
                                <div
                                  className={`${stepCommonClassName.titleContainer} flex-wrap gap-[4px]`}
                                >
                                  {brandItem.brand_img_m ? (
                                    <Image
                                      src={brandItem.brand_img_m}
                                      width={130}
                                      height={21}
                                      className="w-[auto] h-[21px]"
                                      alt="zeelool glass"
                                    />
                                  ) : (
                                    <p>{brandItem.brand_name}</p>
                                  )}
                                  {brandItem.explain && (
                                    <Tooltip
                                      title={
                                        <p
                                          className="text-[14px]"
                                          dangerouslySetInnerHTML={{
                                            __html: processLineBreaks(brandItem.explain),
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                        />
                                      }
                                      placement="bottom"
                                      variant="light"
                                      sx={{
                                        zIndex: 1500,
                                        '& .MuiTooltip-tooltip': {
                                          fontSize: '14px',
                                          maxWidth: '490px',
                                        },
                                      }}
                                    >
                                      <QuestionResponsive
                                        className={`${stepCommonClassName.questionIcon} !ml-[0px]`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          isWap && showExplain(brandItem);
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  {isWap && (brandItem as any).option_img_m && (
                                    <Image
                                      className="w-[auto] h-[16px] ml-[5px] xl:ml-[8px]"
                                      width={102}
                                      height={16}
                                      src={(brandItem as any).option_img_m}
                                      alt=""
                                    />
                                  )}
                                  {!isWap && (brandItem as any).option_img_pc && (
                                    <Image
                                      className="w-[auto] h-[16px] ml-[5px] xl:ml-[8px]"
                                      width={102}
                                      height={16}
                                      src={(brandItem as any).option_img_pc}
                                      alt=""
                                    />
                                  )}

                                  <LensTypePrice
                                    colorList={brandItem.color_list}
                                    hasIndexPrice={hasIndexPrice}
                                    renderPrice={(
                                      price,
                                      sellPrice,
                                      discountPercent,
                                      hasMemberPrice,
                                    ) =>
                                      discountPercent > 0 && (
                                        <div
                                          className={`${stepCommonClassName.discountBadge(
                                            userInfo,
                                            hasMemberPrice,
                                          )} !rounded-[4px] static xl:ml-[4px]`}
                                        >
                                          <span className="relative top-[1px]">
                                            {t('discountPercentOff', { discountPercent })}
                                          </span>
                                        </div>
                                      )
                                    }
                                  />

                                  {hasColorList(brandItem) && isWap && (
                                    <div className="absolute right-0 top-0 text-auxiliary-black">
                                      <RightArrowOutlined
                                        className={stepCommonClassName.arrowIcon}
                                      />
                                    </div>
                                  )}
                                </div>

                                {brandItem.description && (
                                  <p
                                    className={`${stepCommonClassName.description(waiMode)} pr-[10px]`}
                                  >
                                    {brandItem.description}
                                  </p>
                                )}

                                {isWap && getResponsiveBrandTypePrice(brandItem)}

                                {hasColorList(brandItem) && (
                                  <ul className={stepCommonClassName.colorSelector}>
                                    {brandItem.color_list?.map((colorItem) => (
                                      <li
                                        key={colorItem.lens_color}
                                        className={`${stepCommonClassName.colorItem} ${
                                          checkColorSelected(item, brandItem, colorItem)
                                            ? stepCommonClassName.selectedColorItem
                                            : stepCommonClassName.unselectedColorItem
                                        }`}
                                        style={
                                          colorItem.lens_color === LENS_COLOR.NON
                                            ? { display: 'none' }
                                            : {}
                                        }
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onLensBrandColorClick(colorItem, brandItem, item);
                                        }}
                                      >
                                        {isWap ? (
                                          <Image
                                            src={colorItem.option_img}
                                            alt={colorItem.color_name}
                                            width={16}
                                            height={16}
                                          />
                                        ) : (
                                          <Tooltip
                                            title={colorItem.explain || colorItem.color_name}
                                            placement="top"
                                            variant="light"
                                            stopPropagation={false}
                                            sx={{
                                              zIndex: 1500,
                                            }}
                                          >
                                            <Image
                                              src={colorItem.option_img}
                                              alt={colorItem.color_name}
                                              width={16}
                                              height={16}
                                            />
                                          </Tooltip>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              {!isWap && getResponsiveBrandTypePrice(brandItem)}
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </li>
              ))}
            </ul>
          );
        }

        // 无数据时显示空状态
        return (
          <div className={stepCommonClassName.emptyState}>
            <Image
              src="/lens-process/null.png"
              alt=""
              width={118}
              height={118}
              className="xl:w-[168px] xl:h-[134px]"
            />
            <p className={stepCommonClassName.emptyStateText}>
              {t('sorryNoLensesAvailable')}
              {!hasIndexPrice && (
                <>
                  <br />
                  {t('pleaseEditYourPrescription')}
                </>
              )}
            </p>
            {!hasIndexPrice && (
              <Button
                variant="contained"
                className={stepCommonClassName.emptyStateButton}
                onClick={onBackToEditPrescription}
              >
                {t('editPrescription')}
              </Button>
            )}
          </div>
        );
      })()}
      {/* </InfiniteScroll> */}

      {/* 镜片说明弹窗 */}
      <ExplainDialog
        title={explainContent.brand_name || explainContent.lens_name}
        open={showExplainPop}
        onClose={() => setShowExplainPop(false)}
      >
        <p
          className="text-sm font-br"
          dangerouslySetInnerHTML={{
            __html: processLineBreaks(explainContent.explain),
          }}
        />
      </ExplainDialog>

      {/* 底部选择镜片颜色弹窗 */}
      {lensTypeContainer && (
        <Popup
          open={showChooseColor}
          onClose={() => setShowChooseColor(false)}
          childrenMaxHeight="80vh"
          position="bottom"
          className="font-br"
          bodyClassName="!p-[22px_15px_69px]"
          // TODO 这里挂载到body上，在移动端多测试几种机型，确保不会挡住底部按钮
          container={lensTypeContainer}
          round
          zIndex={85}
        >
          {/* TODO 加了这么多东西，仍然无法阻止弹层滚动时带动页面滚动 */}
          <div
            className="relative text-sm text-primary-black box-border overflow-hidden touch-none isolate"
            style={{
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}
            onTouchMove={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onScroll={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <CloseOutlined
              className="absolute top-[0] right-[0] f24 font-bold text-primary-black"
              onClick={() => setShowChooseColor(false)}
            />
            <p className="text-center font-bb text-[16px]">
              {(() => {
                const activeSource = getActiveSource();
                if ((activeSource as BrandItem)?.brand_img_m) {
                  return (
                    <Image
                      className="inline-block w-[auto] h-[21px]"
                      src={(activeSource as BrandItem).brand_img_m}
                      alt=""
                      width={130}
                      height={21}
                    />
                  );
                }
                if ((activeSource as BrandItem)?.brand_name) {
                  return <span>{(activeSource as BrandItem).brand_name}</span>;
                }
                return <span>{(activeSource as LensShowItem)?.lens_name}</span>;
              })()}
            </p>
            <div className="max-h-[500px] overflow-y-auto" onScroll={(e) => e.stopPropagation()}>
              <div className="mt-[20px] flex items-center justify-center">
                {selectedColorItem && (
                  <ProductImageWithLenses
                    className="!w-[196px] h-[98px]"
                    imgUrl={lensGoodsItem?.product?.img || ''}
                    product={{
                      background_image_of_mirror_leg:
                        lensGoodsItem?.product?.image?.background_image_of_mirror_leg?.[0] || '',
                      frame_background_image:
                        lensGoodsItem?.product?.image?.frame_background_image?.[0] || '',
                    }}
                    lens={selectedColorItem}
                  />
                )}
              </div>
              {/* 使用getActiveSource获取当前活跃的数据源 */}
              {getActiveSource()?.description && (
                <p className="p-[25px_20px_27px] text-xs text-[grey] text-auxiliary-black">
                  {getActiveSource().description}
                </p>
              )}
              {Array.isArray(getActiveSource()?.color_list) &&
                getActiveSource()?.color_list.length && (
                  <div className="">
                    <LensTypePrice
                      colorList={getActiveSource()?.color_list}
                      hasIndexPrice={hasIndexPrice}
                      renderPrice={(price, sellPrice) =>
                        Number(sellPrice?.replace(/[^0-9.]/g, '')) > 0 ? (
                          <span className="flex flex-col text-center">
                            <span className={`${stepCommonClassName.priceText} f18`}>
                              {`${sellPrice}`}
                            </span>
                            {price !== sellPrice && (
                              <span
                                className={`${stepCommonClassName.originalPriceText(waiMode)} f12 pt-[8px]`}
                              >
                                {`${price}`}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className={`${stepCommonClassName.priceText} f18`}>
                            {t('free')}
                          </span>
                        )
                      }
                    />
                  </div>
                )}
              <ul
                className={`!justify-center pt-[45px] pb-[10px] ${stepCommonClassName.colorSelector}`}
                onScroll={(e) => e.stopPropagation()}
              >
                {/* 使用getActiveSource获取当前活跃的数据源的颜色列表 */}
                {getActiveSource()?.color_list?.map((colorItem) => (
                  <li
                    key={colorItem.lens_color}
                    className={`${stepCommonClassName.colorItem} mx-[14px] mb-[24px] h-[34px] w-[34px] ${
                      checkColorSelected(selectedLensType, selectedBrand, colorItem)
                        ? stepCommonClassName.selectedColorItem
                        : stepCommonClassName.unselectedColorItem
                    }`}
                    style={colorItem.lens_color === LENS_COLOR.NON ? { display: 'none' } : {}}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPopupLensColorClick(colorItem);
                    }}
                  >
                    <Tooltip
                      title={colorItem.explain || colorItem.color_name}
                      trigger="click"
                      arrow
                      placement="top"
                      variant="light"
                      stopPropagation={false}
                      sx={{
                        zIndex: 1500,
                      }}
                    >
                      <Image
                        src={colorItem.option_img}
                        alt={colorItem.color_name}
                        width={28}
                        height={28}
                      />
                    </Tooltip>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default LensType;
